import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { isAuthenticated, validateToken } from './utils/auth';
import AuthCallback from './pages/AuthCallback';
import Login from './pages/Login';
import SpectacularLogin from './pages/SpectacularLogin';
import SimpleLogin from './pages/SimpleLogin';
import Dashboard from './pages/Dashboard';
import AICommand from './pages/AICommand';
import PersistentIntelligence from './pages/PersistentIntelligence';
import WealthDashboard from './pages/WealthDashboard';
import IntelligenceDashboard from './pages/IntelligenceDashboard';
import InitiativeCommandCenter from './pages/InitiativeCommandCenter';
import CreationStudio from './pages/CreationStudio';
import DocumentVault from './pages/DocumentVault';
import FileManager from './pages/FileManager';
import CreditCommandCenter from './pages/CreditCommandCenter';
import CreditIntelligence from './pages/CreditIntelligence';
import CreditOAuthDemo from './pages/CreditOAuthDemo';
import Opportunities from './pages/Opportunities';
import Integrations from './pages/Settings/Integrations';
import Actions from './pages/Actions';
import ProjectCommandDashboard from './pages/ProjectCommandDashboard';
import ExecutionHub from './pages/ExecutionHub';
import AutonomousBuild from './pages/AutonomousBuild';
import InventionsList from './pages/Inventions/InventionsList';
import ScoutPage from './pages/Inventions/ScoutPage';
import InventionDashboard from './pages/Inventions/InventionDashboard';
import CreateInvention from './pages/CreateInvention';
import AutonomousInventionDashboard from './pages/AutonomousInventionDashboard';
import DebugDashboard from './pages/DebugDashboard';
import TranscendentChat from './pages/TranscendentChat';
import Vault from './pages/Vault/Vault';
import NotificationsCenter from './pages/NotificationsCenter';
import ProgressDashboard from './pages/ProgressDashboard';
import ActivityTimeline from './pages/ActivityTimeline';
import Goals from './pages/Goals';
import LeeMail from './pages/LeeMail';
import Today from './pages/Today';
import ScheduledTasks from './pages/ScheduledTasks';
import BuilderHub from './pages/Inventions/BuilderHub';
import CommandPalette from './components/CommandPalette';
import ProactiveAssistant from './components/ProactiveAssistant';
import './App.css';

const PrivateRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" />;
};

// Google sign-in returns with #session_id=... — intercept before any routing
const HashAuthGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return children;
};

const RootRedirect = () => {
  // U2: post-login landing is now /chat — the primary LEE interface.
  return isAuthenticated() ? <Navigate to="/chat" /> : <Navigate to="/login" />;
};

function App() {
  const [validating, setValidating] = useState(true);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // CMD+K or CTRL+K to open command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    // Validate token on app startup
    const checkAuth = async () => {
      if (isAuthenticated()) {
        await validateToken();
      }
      setValidating(false);
    };
    checkAuth();
  }, []);

  // Show loading screen while validating token
  if (validating) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-5xl font-bold tracking-tighter font-mono text-white mb-4">LEE</h1>
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-muted-foreground mt-4 text-sm">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {/* Command Palette - Global */}
        <CommandPalette 
          isOpen={commandPaletteOpen} 
          onClose={() => setCommandPaletteOpen(false)} 
        />
        
        {/* Proactive AI Assistant - Global */}
        <ProactiveAssistant />
        
        <HashAuthGate>
        <Routes>
          <Route path="/login" element={<SpectacularLogin />} />
          <Route path="/simple-login" element={<SimpleLogin />} />
          <Route path="/old-login" element={<Login />} />
          <Route
            path="/goals"
            element={
              <PrivateRoute>
                <Goals />
              </PrivateRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <PrivateRoute>
                <LeeMail />
              </PrivateRoute>
            }
          />
          <Route
            path="/inventions/:projectId/builders"
            element={
              <PrivateRoute>
                <BuilderHub />
              </PrivateRoute>
            }
          />
          <Route
            path="/scheduled"
            element={
              <PrivateRoute>
                <ScheduledTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/today"
            element={
              <PrivateRoute>
                <Today />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/ai-command"
            element={
              <PrivateRoute>
                <PersistentIntelligence />
              </PrivateRoute>
            }
          />
          <Route
            path="/voice"
            element={<Navigate to="/chat" replace />}
          />
          <Route
            path="/tasks"
            element={<Navigate to="/scheduled" replace />}
          />
          <Route
            path="/settings"
            element={<Navigate to="/settings/integrations" replace />}
          />
          <Route
            path="/voice-old"
            element={<Navigate to="/chat" replace />}
          />
          <Route
            path="/voice-test"
            element={<Navigate to="/chat" replace />}
          />
          <Route
            path="/live-voice"
            element={<Navigate to="/chat" replace />}
          />
          <Route
            path="/intelligence"
            element={
              <PrivateRoute>
                <IntelligenceDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/initiatives"
            element={
              <PrivateRoute>
                <InitiativeCommandCenter />
              </PrivateRoute>
            }
          />
          <Route
            path="/ai-command/old"
            element={
              <PrivateRoute>
                <AICommand />
              </PrivateRoute>
            }
          />
          <Route
            path="/wealth"
            element={
              <PrivateRoute>
                <WealthDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/wealth/control-tower"
            element={<Navigate to="/wealth" replace />}
          />
          <Route
            path="/wealth/command"
            element={<Navigate to="/wealth" replace />}
          />
          <Route
            path="/wealth/old"
            element={<Navigate to="/wealth" replace />}
          />
          <Route
            path="/creation"
            element={
              <PrivateRoute>
                <CreationStudio />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <PrivateRoute>
                <DocumentVault />
              </PrivateRoute>
            }
          />
          <Route
            path="/files"
            element={
              <PrivateRoute>
                <FileManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/credit"
            element={
              <PrivateRoute>
                <CreditIntelligence />
              </PrivateRoute>
            }
          />
          <Route
            path="/credit/demo"
            element={
              <PrivateRoute>
                <CreditCommandCenter />
              </PrivateRoute>
            }
          />
          <Route
            path="/credit/oauth-demo"
            element={
              <PrivateRoute>
                <CreditOAuthDemo />
              </PrivateRoute>
            }
          />
          <Route
            path="/opportunities"
            element={
              <PrivateRoute>
                <Opportunities />
              </PrivateRoute>
            }
          />
          <Route
            path="/actions"
            element={
              <PrivateRoute>
                <Actions />
              </PrivateRoute>
            }
          />
          <Route
            path="/execution"
            element={
              <PrivateRoute>
                <ExecutionHub />
              </PrivateRoute>
            }
          />
          <Route
            path="/execution/project/:planId"
            element={
              <PrivateRoute>
                <ProjectCommandDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/autonomous-build"
            element={
              <PrivateRoute>
                <AutonomousBuild />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/integrations"
            element={
              <PrivateRoute>
                <Integrations />
              </PrivateRoute>
            }
          />
          <Route
            path="/inventions"
            element={
              <PrivateRoute>
                <InventionsList />
              </PrivateRoute>
            }
          />
          <Route
            path="/scout"
            element={
              <PrivateRoute>
                <ScoutPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/inventions/new"
            element={
              <PrivateRoute>
                <CreateInvention />
              </PrivateRoute>
            }
          />
          <Route
            path="/inventions/:id"
            element={
              <PrivateRoute>
                <AutonomousInventionDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/vault"
            element={
              <PrivateRoute>
                <Vault />
              </PrivateRoute>
            }
          />
          
          {/* Notifications & Progress Routes */}
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <NotificationsCenter />
              </PrivateRoute>
            }
          />
          <Route
            path="/progress"
            element={
              <PrivateRoute>
                <ProgressDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <PrivateRoute>
                <ActivityTimeline />
              </PrivateRoute>
            }
          />
          <Route
            path="/debug"
            element={
              <PrivateRoute>
                <DebugDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/talk"
            element={
              <PrivateRoute>
                <TranscendentChat />
              </PrivateRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <PrivateRoute>
                <TranscendentChat />
              </PrivateRoute>
            }
          />

          <Route path="/" element={<RootRedirect />} />
        </Routes>
        </HashAuthGate>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            color: '#FFFFFF',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />
    </div>
  );
}

export default App;
